"use client";

import React, { useCallback, useMemo, useState } from "react";
import { RiAlertLine } from "react-icons/ri";

import {
  APISearchSelect,
  APISearchSelectContent,
  type APISearchSelectRemoteResults,
  APISearchSelectTrigger,
} from "@/components/ui/select/api-search-select";
import Tooltip from "@/components/ui/tooltip";

import type { CPTCode } from "@/types/medical-codes";

/**
 * Creatable combobox for medical codes (CPT / modifiers / ICD-10 / POS).
 *
 * The shared `APISearchSelect` is API-backed but pick-only; `SearchSelect`'s
 * `onCreateOption` only works off a static options array. Billing code fields
 * need both — API-backed suggestions AND the ability to keep a typed value
 * that isn't in the reference table (HCPCS codes like `G0463`, alphanumeric
 * modifiers like `GT`/`95`). So we compose `APISearchSelect` WITHOUT editing
 * it: the caller's `onSearch` is wrapped to inject a synthetic
 * "Use '<typed value>'" option whenever the query has no exact code match, and
 * `APISearchSelectContent` renders it as a normal pickable row.
 *
 * Pattern reference: medical-codes-panel.tsx.
 */

/** A code row, optionally flagged as the synthetic "create" affordance. */
export type CreatableOption = CPTCode & { __create?: boolean };

interface CommonProps {
  /** Fetch API suggestions for a query (e.g. searchCPT / searchModifiers). */
  onSearch?: (query: string) => Promise<CPTCode[] | null>;
  /** Caller-driven paged results (e.g. `useIcd10CodeOptionSearch`), in place
   *  of `onSearch`; the list loads further pages as it scrolls. */
  remote?: APISearchSelectRemoteResults<CPTCode>;
  placeholder?: string;
  disabled?: boolean;
  emptyMessage?: string;
  /** Run an empty search + open the list on focus so it's browsable. Only
   *  sensible for small option sets (modifiers, POS), not the full CPT table. */
  browseOnFocus?: boolean;
  /** Optional per-code warning: return tooltip text to flag a code (amber chip +
   *  hover glyph), or `undefined` to leave it unflagged. Used by the Super Bill
   *  editor to warn on modifiers outside the billing code's crosswalk without
   *  removing them. */
  optionWarning?: (code: string) => string | undefined;
  className?: string;
}

interface SingleProps extends CommonProps {
  isMulti?: false;
  /** The currently-stored code (e.g. row.billingCode). */
  value?: string;
  /** Receives the picked option — a real row (carries a description) or the
   *  synthetic custom entry (description ""). */
  onSelect: (picked: CPTCode) => void;
  /** Empty the box after each pick, for callers that list the picked codes
   *  themselves instead of holding one in the box. */
  clearAfterSelect?: boolean;
  /** Codes the caller already lists: left out of the results, and never
   *  offered again as "Use '<typed>'". */
  excludeCodes?: string[];
}

interface MultiProps extends CommonProps {
  isMulti: true;
  /** The currently-stored codes (e.g. row.modifiers). */
  values: string[];
  /** Emits the next list of codes. */
  onValuesChange: (codes: string[]) => void;
  /** Optional code -> description map so chips read "code — description"
   *  instead of the bare code. Codes missing from the map show bare. */
  descriptions?: Record<string, string>;
}

type CreatableCodeSelectProps = SingleProps | MultiProps;

export type CreatableCodeSelectSingleProps = SingleProps;

export type CreatableCodeSelectMultiProps = MultiProps;

export function formatName(option: CreatableOption): string {
  if (option.__create) return `Use "${option.code}"`;
  // Real/known rows show "code — description"; synthetic-but-stored or bare
  // codes (HCPCS not in the table) show just the code, no trailing "— ".
  return option.description
    ? `${option.code} — ${option.description}`
    : option.code;
}

const NO_CODES: string[] = [];

/**
 * Prepend the "Use '<typed>'" row unless a result is already the typed code.
 * Dot-insensitive: ICD-10 search matches "M25511" to the stored "M25.511", so
 * that row is the exact match and must not be shadowed by a "Use 'M25511'" row
 * that would save a code absent from the table. CPT codes and modifiers carry
 * no dots, so they compare as before.
 *
 * `excludeCodes` are dropped from the results but still count as matches, so
 * typing a code that is already listed never offers it again.
 */
export function withCreateOption(
  results: CPTCode[],
  query: string,
  excludeCodes: string[] = NO_CODES,
): CreatableOption[] {
  const bare = (code: string) => code.replace(/\./g, "").toLowerCase();
  const excluded = new Set(excludeCodes.map(bare));
  const shown = excluded.size
    ? results.filter((r) => !excluded.has(bare(r.code)))
    : results;
  const trimmed = query.trim();
  if (!trimmed) return shown;
  if (
    excluded.has(bare(trimmed)) ||
    results.some((r) => bare(r.code) === bare(trimmed))
  ) {
    return shown;
  }
  const synthetic: CreatableOption = {
    code: trimmed.toUpperCase(),
    description: "",
    __create: true,
  };
  return [synthetic, ...shown];
}

/**
 * Wrap the caller's onSearch: inject a synthetic "Use '<typed>'" option when
 * the trimmed query is non-empty and no API result already is the typed code
 * (see `withCreateOption`). This is the free-text escape hatch for HCPCS codes
 * and alphanumeric modifiers that aren't in the reference tables. Shared with
 * the `CptCodeSelect` variant.
 */
export function useCreatableSearch(
  onSearch: ((query: string) => Promise<CPTCode[] | null>) | undefined,
  excludeCodes: string[] = NO_CODES,
) {
  return useCallback(
    async (query: string): Promise<CreatableOption[] | null> =>
      withCreateOption((await onSearch?.(query)) ?? [], query, excludeCodes),
    [onSearch, excludeCodes],
  );
}

/**
 * Bridge a stored code to the option APISearchSelect controls. Keyed on the
 * primitive so a fresh object each parent render doesn't fight the select's
 * controlled `value` effect (which would overwrite in-progress typing).
 */
export function useSingleCodeValue(code: string | undefined) {
  return useMemo<CreatableOption | undefined>(
    () => (code ? { code, description: "" } : undefined),
    [code],
  );
}

export const CreatableCodeSelect: React.FC<CreatableCodeSelectProps> = (
  props,
) => {
  const {
    onSearch,
    placeholder,
    disabled,
    emptyMessage,
    browseOnFocus,
    optionWarning,
    remote,
  } = props;

  // Keyed on a string so a fresh array each parent render keeps the search
  // callbacks stable.
  const excludeKey = props.isMulti ? "" : (props.excludeCodes ?? []).join("\n");
  const excludeCodes = useMemo(
    () => (excludeKey ? excludeKey.split("\n") : NO_CODES),
    [excludeKey],
  );

  const wrappedSearch = useCreatableSearch(onSearch, excludeCodes);

  // Remote mode: same "Use '<typed>'" row, prepended to the caller's paged
  // results. The typed term is tracked here because the caller only sees it
  // through `onSearchTermChange`. The row waits for the results: the term
  // updates on every keystroke but the results only after the debounced fetch,
  // so offering it earlier could put it alone above a real match that is about
  // to arrive, one Enter away from saving a code that isn't in the table.
  const [remoteTerm, setRemoteTerm] = useState("");
  const remoteOnSearchTermChange = remote?.onSearchTermChange;
  const handleRemoteTermChange = useCallback(
    (term: string) => {
      setRemoteTerm(term);
      remoteOnSearchTermChange?.(term);
    },
    [remoteOnSearchTermChange],
  );
  const remoteResults = remote?.results;
  const remoteWithCreate = useMemo<
    APISearchSelectRemoteResults<CreatableOption> | undefined
  >(
    () =>
      remote && remoteResults
        ? {
            ...remote,
            results: remote.isLoading
              ? withCreateOption(remoteResults, "", excludeCodes)
              : withCreateOption(remoteResults, remoteTerm, excludeCodes),
            onSearchTermChange: handleRemoteTermChange,
          }
        : undefined,
    [remote, remoteResults, remoteTerm, handleRemoteTermChange, excludeCodes],
  );

  // Flag codes the caller's `optionWarning` rejects: amber container (chip +
  // dropdown row, via APISearchSelect's `containerClassName`) and an
  // `optionNode` carrying a warning glyph + hover tooltip. Unflagged codes fall
  // back to the plain `{id, name}` shape so existing consumers are untouched.
  const formatOption = useCallback(
    (option: CreatableOption) => {
      const name = formatName(option);
      const warning = optionWarning?.(option.code);
      if (!warning) return { id: option.code, name };
      return {
        id: option.code,
        name,
        containerClassName: "bg-status-warning-bg text-status-warning-text",
        optionNode: (
          <Tooltip content={warning} side="top" triggerAsChild>
            <span className="flex items-center gap-1">
              <RiAlertLine className="h-3.5 w-3.5 shrink-0" />
              {name}
            </span>
          </Tooltip>
        ),
      };
    },
    [optionWarning],
  );

  // Bridge stored code(s) <-> CreatableOption(s). Both memos run on every
  // render (hooks must be unconditional) but are keyed on stable primitives so
  // a fresh array each parent render doesn't fight APISearchSelect's controlled
  // `value`/`values` effects (which would otherwise overwrite in-progress
  // typing or thrash setState).
  const bridgedValue = useSingleCodeValue(
    props.isMulti ? undefined : props.value,
  );

  const multiCodesKey = props.isMulti
    ? JSON.stringify(
        props.values.map((code) => [code, props.descriptions?.[code] ?? ""]),
      )
    : "";
  const bridgedValues = useMemo<CreatableOption[]>(
    () =>
      multiCodesKey
        ? (JSON.parse(multiCodesKey) as [string, string][]).map(
            ([code, description]) => ({ code, description }),
          )
        : [],
    [multiCodesKey],
  );

  const trigger = (
    <APISearchSelectTrigger
      placeholder={placeholder}
      disabled={disabled}
      openOnFocus={browseOnFocus}
      className={props.className}
    />
  );
  const content = (
    <APISearchSelectContent emptyMessage={emptyMessage ?? "No codes found"} />
  );

  if (props.isMulti) {
    const { onValuesChange } = props;
    return (
      <APISearchSelect<CreatableOption>
        isMulti
        values={bridgedValues}
        onValuesChange={(next) => onValuesChange(next.map((o) => o.code))}
        onSearch={remoteWithCreate ? undefined : wrappedSearch}
        remote={remoteWithCreate}
        formatOption={formatOption}
      >
        {trigger}
        {content}
      </APISearchSelect>
    );
  }

  const { onSelect, clearAfterSelect } = props;
  return (
    <APISearchSelect<CreatableOption>
      value={bridgedValue}
      clearAfterSelect={clearAfterSelect}
      onValueChange={(picked) => {
        if (!picked) return;
        onSelect(picked);
        // The remote list must follow the emptied box, or it keeps showing the
        // last term's results (and its "Use" row) under an empty input.
        if (clearAfterSelect) handleRemoteTermChange("");
      }}
      onSearch={remoteWithCreate ? undefined : wrappedSearch}
      remote={remoteWithCreate}
      formatOption={formatOption}
    >
      {trigger}
      {content}
    </APISearchSelect>
  );
};
